import React, { useState, useEffect } from 'react';
import { useStripe, Elements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';
import { FaCheck, FaExclamationTriangle, FaHourglassHalf } from 'react-icons/fa';
import './SubscriptionSuccessPage.css';
import { loadStripe } from '@stripe/stripe-js';

// Load Stripe outside of the component to avoid re-creating it on every render.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const SubscriptionSuccessStatus = () => {
  const stripe = useStripe();
  const [status, setStatus] = useState('loading');
  const [title, setTitle] = useState('Verifying Payment');
  const [message, setMessage] = useState('Please wait while we confirm your payment details.');

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const paymentIntentClientSecret = params.get('payment_intent_client_secret');
    const setupIntentClientSecret = params.get('setup_intent_client_secret');

    if (paymentIntentClientSecret) {
      // Handle PaymentIntent verification
      stripe.retrievePaymentIntent(paymentIntentClientSecret).then(({ paymentIntent }) => {
        switch (paymentIntent.status) {
          case 'succeeded':
            setStatus('success');
            setTitle('Payment Successful!');
            setMessage('Your subscription is now active. You can now access all the premium features.');
            break;
          case 'processing':
            setStatus('processing');
            setTitle('Payment Processing');
            setMessage('Your payment is being processed. We will notify you once it is complete.');
            break;
          case 'requires_payment_method':
            setStatus('error');
            setTitle('Payment Failed');
            setMessage('Your payment could not be processed. Please return and try another payment method.');
            break;
          default:
            setStatus('error');
            setTitle('An Error Occurred');
            setMessage('Something went wrong while processing your payment. Please contact support.');
            break;
        }
      });
    } else if (setupIntentClientSecret) {
      // Handle SetupIntent verification
      stripe.retrieveSetupIntent(setupIntentClientSecret).then(({ setupIntent }) => {
        switch (setupIntent.status) {
          case 'succeeded':
            setStatus('success');
            setTitle('Subscription Confirmed!');
            setMessage('Your payment method has been saved and your subscription is now active.');
            break;
          case 'processing':
            setStatus('processing');
            setTitle('Setup Processing');
            setMessage('We are setting up your payment method. We will notify you once it is complete.');
            break;
          default:
            setStatus('error');
            setTitle('Setup Failed');
            setMessage('We could not save your payment method. Please return and try again.');
            break;
        }
      });
    } else {
      // Handle cases where no client secret is present
      setStatus('error');
      setTitle('Verification Failed');
      setMessage('Could not find a payment to verify. If you believe this is an error, please contact support.');
    }
  }, [stripe]);

  const renderContent = () => {
    switch (status) {
      case 'success':
        return (
          <div className="status-icon success-icon">
            <FaCheck />
          </div>
        );
      case 'error':
        return (
          <div className="status-icon error-icon">
            <FaExclamationTriangle />
          </div>
        );
      case 'processing':
        return (
          <div className="status-icon processing-icon">
            <FaHourglassHalf />
          </div>
        );
      default:
        return <div className="spinner"></div>;
    }
  };

  return (
    <div className="status-page-container">
      <div className="status-card">
        {renderContent()}
        <h1 className="status-title">{title}</h1>
        <p className="status-message">{message}</p>
        {(status === 'success' || status === 'processing') && (
          <Link to="/seeker-dashboard" className="return-button">
            Go to Dashboard
          </Link>
        )}
        {status === 'error' && (
          <Link to="/subscription" className="return-button">
            Try Again
          </Link>
        )}
      </div>
    </div>
  );
};

const SubscriptionSuccessPage = () => (
  <Elements stripe={stripePromise}>
    <SubscriptionSuccessStatus />
  </Elements>
);

export default SubscriptionSuccessPage;
